import { DFPEmpty } from "@/components/empty/dfp-empty";
import { DFPTable } from "@/components/tables/dfp-table";
import {
	ASCENDING,
	type BaseTableAction,
	type BaseTableState,
	baseInitialState,
	baseTableReducer,
} from "@/components/tables/shared-table-state";
import { getOrganizationUsers } from "@/services/user-service";
import { UserAccessLevel, useUserStore } from "@/stores/user-store";
import {
	addSortOrderToColumns,
	createTableChangeHandler,
	sortDataClientSide,
} from "@/utils/table-sort-utils";
import { EditOutlined, MailOutlined } from "@ant-design/icons";
import { Button, Divider, Input, Tooltip } from "antd";
import { type Dispatch, type FC, useEffect, useReducer, useState } from "react";
import { getColumns } from "./UsersTableColumns";
import { EditUserModal } from "./children/EditUserModal";
import { InviteUserModal } from "./children/InviteUserModal";

const { Search } = Input;

export interface User {
	id: string | number;
	name: string;
	email: string;
	role: string;
	role_id: number;
	organization_id: number;
	[key: string]: any;
}

// Define action types
export type Action =
	| { type: "SET_ALL_USERS"; payload: User[] }
	| { type: "SET_SHOW_ADD_USER_MODAL"; payload: boolean }
	| { type: "SET_SHOW_DELETE_MODAL"; payload: boolean }
	| { type: "SET_SHOW_INVITE_MODAL"; payload: boolean }
	| BaseTableAction;

// Initial state
export interface State extends BaseTableState {
	allUsers: User[];
	showAddUserModal: boolean;
	showDeleteModal: boolean;
	showInviteModal: boolean;
}

const initialState: State = {
	...baseInitialState,
	allUsers: [],
	sortBy: "name",
	sortOrder: ASCENDING,
	showAddUserModal: false,
	showDeleteModal: false,
	showInviteModal: false,
};

function reducer(state: State, action: Action): State {
	switch (action.type) {
		case "SET_ALL_USERS":
			return { ...state, allUsers: action.payload };
		case "SET_SHOW_ADD_USER_MODAL":
			return { ...state, showAddUserModal: action.payload };
		case "SET_SHOW_DELETE_MODAL":
			return { ...state, showDeleteModal: action.payload };
		case "SET_SHOW_INVITE_MODAL":
			return { ...state, showInviteModal: action.payload };
		default:
			return baseTableReducer(state, action);
	}
}

const UsersTable: FC = () => {
	// Generate columns
	const [state, dispatch] = useReducer(reducer, initialState);
	const {
		sortBy,
		sortOrder,
		pageNum,
		pageSize,
		searchVal,
		isLoading,
		allUsers,
	} = state;
	const user = useUserStore((state: any) => state.user);
	const organization = user?.organization?.id;

	const [editingUser, setEditingUser] = useState<any>(null);
	const [showEditModal, setShowEditModal] = useState(false);

	const userAccessLevel = user?.role?.accessLevel ?? user?.role?.access_level ?? 0;
	const isAdmin = userAccessLevel >= UserAccessLevel.ADMIN;

	const getUsers = async (dispatch: Dispatch<Action>) => {
		dispatch({ type: "SET_IS_LOADING", payload: true });
		const users = await getOrganizationUsers();
		dispatch({ type: "SET_ALL_USERS", payload: users });
	};

	const getRows = (state: State, dispatch: Dispatch<Action>) => {
		// Client-side search and sort
		dispatch({ type: "SET_IS_LOADING", payload: true });

		// Step 1: Apply search filter
		let filteredUsers = allUsers;
		if (state.searchVal) {
			filteredUsers = allUsers.filter((user) => {
				return Object.values(user).some((value) =>
					value
						?.toString()
						.toLowerCase()
						.includes(state.searchVal.toLowerCase()),
				);
			});
		}

		// Step 2: Apply client-side sorting using the unified utility
		const sortedUsers = sortDataClientSide(
			filteredUsers,
			state.sortBy,
			state.sortOrder,
		);

		dispatch({ type: "SET_DATA_SOURCE", payload: sortedUsers });
		dispatch({ type: "SET_IS_LOADING", payload: false });
	};

	useEffect(() => {
		const baseColumns = getColumns(
			() => getUsers(dispatch),
			organization,
			user?.role_id,
		);

		// Add actions column if user is admin
		const columns = isAdmin
			? [
					...baseColumns,
					{
						title: "",
						key: "actions",
						width: 50,
						render: (_: any, record: any) => {
							if (record.isLoading) return null;
							return (
								<Tooltip title="Edit user">
									<Button
										type="text"
										icon={<EditOutlined />}
										onClick={() => {
											setEditingUser(record);
											setShowEditModal(true);
										}}
										size="small"
									/>
								</Tooltip>
							);
						},
					},
				]
			: baseColumns;

		dispatch({
			type: "SET_TABLE_COLUMNS",
			payload: columns,
		});
		dispatch({
			type: "SET_COLUMNS_SHOWN",
			payload: columns.reduce((acc: Record<string, boolean>, col: any) => {
				acc[col.key] = true;
				return acc;
			}, {}),
		});
	}, [dispatch, organization, user?.role_id, isAdmin]);

	useEffect(() => {
		getUsers(dispatch);
	}, [organization]);

	useEffect(() => {
		if (allUsers.length > 0) {
			getRows(state, dispatch);
		}
	}, [pageNum, pageSize, sortBy, sortOrder, searchVal, allUsers]);

	// Add sort order indicators to visible columns for controlled sorting
	// This ensures the correct sort arrow appears on the active column header
	const visibleColumns = state.tableColumns.filter(
		(col: any) => state.columnsShown[col.key],
	);
	const columnsWithSortOrder = addSortOrderToColumns(
		visibleColumns,
		sortBy,
		sortOrder,
	);

	return (
		<div className="table-card">
			<div className="table-header d-flex justify-content-between">
				<Search
					placeholder="Search"
					allowClear
					style={{ width: 300 }}
					onSearch={(value: string) => {
						dispatch({ type: "SET_SEARCH_VAL", payload: value });
					}}
					disabled={isLoading}
				/>
				<Button
					icon={<MailOutlined />}
					onClick={() => dispatch({ type: "SET_SHOW_INVITE_MODAL", payload: true })}
					style={{ backgroundColor: '#92278F', borderColor: '#92278F', color: '#fff' }}
				>
					Invite User
				</Button>
			</div>
			<Divider />
			<DFPTable
				loading={state.isLoading}
				dataSource={state.dataSource}
				columns={columnsWithSortOrder}
				rowKey={(record: any) => record.id}
				pagination={{
					current: state.pageNum,
					pageSize: state.pageSize,
					total: state.dataSource.length,
					onChange: (page, pageSize) => {
						dispatch({ type: "SET_PAGE_NUM", payload: page });
						dispatch({ type: "SET_PAGE_SIZE", payload: pageSize || 10 });
					},
				}}
				scroll={{ x: 1100 }}
				// Add onChange handler for sorting
				onChange={createTableChangeHandler({ pageNum, pageSize }, dispatch)}
				locale={{
					emptyText: !state.isLoading && (
						<DFPEmpty
							className="w-100 h-100"
							description="No users found. Please create a new user, or adjust your search filters."
						/>
					),
				}}
			/>
			<InviteUserModal
				open={state.showInviteModal}
				onCancel={() => dispatch({ type: "SET_SHOW_INVITE_MODAL", payload: false })}
				onSuccess={() => {
					dispatch({ type: "SET_SHOW_INVITE_MODAL", payload: false });
					getUsers(dispatch);
				}}
			/>
			<EditUserModal
				open={showEditModal}
				user={editingUser}
				onCancel={() => {
					setShowEditModal(false);
					setEditingUser(null);
				}}
				onSuccess={() => {
					setShowEditModal(false);
					setEditingUser(null);
					getUsers(dispatch);
				}}
			/>
		</div>
	);
};

export { UsersTable };
